import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ChevronDown } from "lucide-react";

// Services & Actions
import { getWssCountApi } from "@services/wss/getWssCountApi";
import { setCheckInFlag } from "@redux/actions/wss-actions";

// Components
import CommonSnackbar from "@components/Common/CommonSnackbar";

// Constants
import { myWssLabel } from "@components/Common/Constant";

const InitialCount = [
  { Type: "Total", value: "0" },
  { Type: "Visited", value: "0" },
  { Type: "Not Visited", value: "0" },
  { Type: "New", value: "0" },
];

const countFilterLabel = [
  { dropdownItems: "Quarterly", id: 0, filterValue: "3" },
  { dropdownItems: "Bi-annual", id: 1, filterValue: "6" },
];

/**
 * MyWssCounts Component
 * Displays WSS (Wholesale & Stockist Shop) count statistics with filtering options
 * Refactored with Tailwind CSS
 * 
 * @component
 */
const MyWssCounts = () => {
  const dispatch = useDispatch();

  // Redux State
  const ischeckInFlag = useSelector((state) => state?.checkInFlag?.wssCountFlag);
  const territoryList = useSelector((state) => state?.territory?.territoryDetails);

  // Local State
  const [selectedFilter, setSelectedFilter] = useState(countFilterLabel[0]);
  const [wssCountData, setWssCountData] = useState(InitialCount);
  const [wssNotVisitedCount, setWssNotVisitedCount] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [failedOpen, setFailedOpen] = useState(false);
  const [wssCountFlag, setWssCountFlag] = useState(false);

  // Fetch WSS Counts
  useEffect(() => {
    (async () => {
      if (selectedFilter.filterValue && territoryList && territoryList.length > 0) {
        const response = await getWssCountApi(territoryList, selectedFilter.filterValue);
        if (response?.success) {
          setWssCountData(response?.data);
          setWssNotVisitedCount(response?.data[2]?.value || "0");
        }
      }
    })();
  }, [selectedFilter, territoryList]);

  // Show count alert after login
  useEffect(() => {
    if (ischeckInFlag === true && wssNotVisitedCount > 0) {
      setFailedOpen(true);
    }
  }, [wssNotVisitedCount, ischeckInFlag]);

  // Avoid alert repeatedly after login
  useEffect(() => {
    if (wssCountFlag === true) {
      dispatch(setCheckInFlag(false));
    }
  }, [wssCountFlag, dispatch]);

  // Handle snackbar close
  const handleOnClose = (reason) => {
    if (reason === "clickaway") return;
    setFailedOpen(false);
    setWssCountFlag(true);
  };

  // Handle filter change
  const handleFilterChange = (filter) => {
    setSelectedFilter(filter);
    setDropdownOpen(false);
  };

  return (
    <>
      {/* Warning Snackbar for pending WSS visits */}
      {ischeckInFlag && wssNotVisitedCount > 0 && (
        <CommonSnackbar
          open={failedOpen}
          autoHideDuration={2000}
          onClose={handleOnClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          severity="warning"
          message={`${wssNotVisitedCount} WSS pending for visit in this quarter`}
        />
      )}

      <div className="w-[94%] mx-2 my-2 mb-6 h-32 bg-white rounded-xl shadow-[0.056rem_0.056rem_0.241rem_rgba(20,20,20,0.07)]">
        {/* Header */}
        <div className="w-full h-[2.125rem] bg-[#3901FF] rounded-t-xl flex items-center justify-between px-5">
          <div className="text-white font-semibold text-[0.688rem] leading-8">
            My Wss
          </div>

          {/* Dropdown Filter */}
          <div className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1.5 bg-transparent border-0 text-white text-xs font-semibold cursor-pointer px-0 py-0"
              aria-label="Filter WSS counts"
            >
              <span>{selectedFilter.dropdownItems}</span>
              <ChevronDown size={14} />
            </button>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div
                className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg z-10 min-w-[6.5rem] py-1"
                style={{ boxShadow: "0.9px 0.9px 3.86px rgba(20, 20, 20, 0.07)" }}
              >
                {countFilterLabel.map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => handleFilterChange(filter)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
                  >
                    <input
                      type="radio"
                      checked={selectedFilter.id === filter.id}
                      onChange={() => {}}
                      className="w-[15px] h-[15px] accent-[#2CC26A]"
                    />
                    <label className="text-[11.83px] font-medium text-gray-700 cursor-pointer">
                      {filter.dropdownItems}
                    </label>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Data Grid */}
        <div className="pt-3">
          <div className="grid grid-cols-4 text-center gap-2 px-2">
            {/* Numbers */}
            {wssCountData.length !== 0 &&
              wssCountData?.map((item, index) => (
                <div
                  key={index}
                  className="text-[#3901FF] font-extrabold text-[1.227rem] -mb-1"
                >
                  {item.value}
                </div>
              ))}

            {/* Labels */}
            {myWssLabel?.map((items, index) => (
              <div key={index}>
                <div className="text-[#3901FF] font-bold text-[0.738rem] max-[370px]:text-[0.61rem] text-center">
                  {items.heading}
                </div>
                <div className="text-[#595959] font-medium text-[0.488rem] whitespace-nowrap px-1">
                  {items.heading_second}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Close dropdown when clicking outside */}
      {dropdownOpen && (
        <div
          className="fixed inset-0 z-[9]"
          onClick={() => setDropdownOpen(false)}
        />
      )}
    </>
  );
};

export default MyWssCounts;
